import { memo, useCallback, useState } from 'react';
import {
  DotsSixVerticalIcon,
  EyeClosedIcon,
  EyeIcon,
  PushPinSimpleSlashIcon,
} from '@phosphor-icons/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@admin-panel/ui';
import GroupImage from './GroupImage';
import LookGroupLooksPanel from './LookGroupLooksPanel';
import GroupMeta from './GroupMeta';
import type { LookGroupListItem, ProductDepthSummary } from './types';

type SortablePinnedItemProps = {
  item: LookGroupListItem;
  isBusy: boolean;
  onUnpin: (groupId: number) => Promise<void>;
  elementRef?: (node: HTMLElement | null) => void;
  productDepth?: ProductDepthSummary;
};

type PinnedGroupCardContentProps = {
  item: LookGroupListItem;
  isBusy: boolean;
  onUnpin: (groupId: number) => Promise<void>;
  productDepth?: ProductDepthSummary;
  isLooksOpen: boolean;
  onToggleLooks: () => void;
};

const PinnedGroupCardContent = memo(
  ({
    item,
    onUnpin,
    isBusy,
    productDepth,
    isLooksOpen,
    onToggleLooks,
  }: PinnedGroupCardContentProps) => {
    const previewTitle = item.preview?.title || `Look Group #${item.group.id}`;
    const previewImage = item.preview?.imageUrl;

    return (
      <>
        <GroupImage title={previewTitle} imageUrl={previewImage} />

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="m-0 text-sm font-semibold text-foreground">{previewTitle}</p>
            </div>

            <Button
              size="small"
              variant="ghost"
              icon={<PushPinSimpleSlashIcon size={14} />}
              isLoading={isBusy}
              onClick={() => onUnpin(item.group.id)}
            >
              Unpin
            </Button>
          </div>

          <GroupMeta item={item} productDepth={productDepth} />
          <div className="flex justify-end pt-1">
            <Button
              size="small"
              variant="outline"
              icon={isLooksOpen ? <EyeIcon size={14} /> : <EyeClosedIcon size={14} />}
              onClick={onToggleLooks}
            >
              {isLooksOpen ? 'Hide Looks' : 'Show Looks'}
            </Button>
          </div>
        </div>
      </>
    );
  },
);

PinnedGroupCardContent.displayName = 'PinnedGroupCardContent';

const SortablePinnedItem = ({
  item,
  onUnpin,
  isBusy,
  elementRef,
  productDepth,
}: SortablePinnedItemProps) => {
  const [isLooksOpen, setIsLooksOpen] = useState(false);
  const handleToggleLooks = useCallback(() => {
    setIsLooksOpen(previous => !previous);
  }, []);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.group.id,
    transition: {
      duration: 240,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)',
    zIndex: isDragging ? 20 : undefined,
    cursor: isDragging ? 'grabbing' : undefined,
  };

  return (
    <article
      ref={node => {
        setNodeRef(node);
        elementRef?.(node);
      }}
      style={style}
      className="rounded-xl border border-border bg-card p-3 will-change-transform"
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Reorder pinned look group"
          className="mt-1 inline-flex h-7 w-7 cursor-grab items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground transition-colors hover:bg-muted active:cursor-grabbing"
        >
          <DotsSixVerticalIcon size={15} />
        </button>

        <PinnedGroupCardContent
          item={item}
          onUnpin={onUnpin}
          isBusy={isBusy}
          productDepth={productDepth}
          isLooksOpen={isLooksOpen}
          onToggleLooks={handleToggleLooks}
        />
      </div>
      <LookGroupLooksPanel groupId={item.group.id} open={isLooksOpen} />
    </article>
  );
};

export default SortablePinnedItem;
